import React from "react";
import { Stack, Box, Divider } from "@mui/joy";
import type { EquipmentModel, AttributeOption } from "../../../api/equipment/Equipment.typed";
import { SCreate, SDelete } from "../../../styles/StyledMUI";
import CommonModal from "../../common/CommonModal";
import IconButtonCTA from "../../common/IconButtonCTA";
import DarkInput from "../../common/DarkInput";
import DarkTypography from "../../common/DarkTypography";
import { colorTokens } from "../../../styles/globalStyles";

interface EquipmentModelOptionsModalProps {
  open: boolean;
  onClose: () => void;
  model: EquipmentModel | null;
  optionIdx: number | null;
  onModelChange: (model: EquipmentModel) => void;
}

export default function EquipmentModelOptionsModal({
  open,
  onClose,
  model,
  optionIdx,
  onModelChange,
}: EquipmentModelOptionsModalProps) {
  const [newOptionLabel, setNewOptionLabel] = React.useState("");

  const addOption = () => {
    if (newOptionLabel.trim() === "" || optionIdx === null || !model) return;
    const option = { ID: crypto.randomUUID(), Label: newOptionLabel.trim() };
    const attrs = [...(model.Attributes || [])];
    const attr = attrs[optionIdx];
    attr.Options = attr.Options ? [...attr.Options, option] : [option];
    attrs[optionIdx] = attr;
    onModelChange({ ...model, Attributes: attrs });
    setNewOptionLabel("");
  };

  const removeOption = (optId: string) => {
    if (optionIdx === null || !model) return;
    const attrs = [...(model.Attributes || [])];
    const attr = attrs[optionIdx];
    attr.Options = (attr.Options || []).filter((o: AttributeOption) => o.ID !== optId);
    attrs[optionIdx] = attr;
    onModelChange({ ...model, Attributes: attrs });
  };

  const handleClose = () => {
    setNewOptionLabel("");
    onClose();
  };

  if (!model || optionIdx === null) return null;

  return (
    <CommonModal open={open} onClose={handleClose} title="Options" size="sm">
      <Box
        sx={{
          maxHeight: 300,
          overflowY: "auto",
          borderRadius: "sm",
          backgroundColor: colorTokens.bg.app,
          p: 0.75,
        }}
      >
        <Stack spacing={1}>
          {(model.Attributes || [])[optionIdx]?.Options?.map((opt: AttributeOption) => (
            <Box
              key={opt.ID}
              sx={{
                p: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                borderRadius: "sm",
                backgroundColor: colorTokens.bg.surfaceMuted,
                border: `1px solid ${colorTokens.border.default}`,
              }}
            >
              <DarkTypography sx={{ flexGrow: 1 }}>{opt.Label}</DarkTypography>
              <IconButtonCTA
                ctaVariant="danger"
                size="sm"
                aria-label="Supprimer option"
                onClick={() => removeOption(opt.ID)}
              >
                <SDelete />
              </IconButtonCTA>
            </Box>
          ))}
        </Stack>
        <Divider sx={{ my: 1, borderColor: colorTokens.border.default }} />
        <Stack direction="row" spacing={1}>
          <DarkInput
            placeholder="Libellé"
            value={newOptionLabel}
            onChange={(e) => setNewOptionLabel(e.target.value)}
            sx={{ flexGrow: 1 }}
          />
          <IconButtonCTA ctaVariant="success" aria-label="Ajouter option" onClick={addOption}>
            <SCreate />
          </IconButtonCTA>
        </Stack>
      </Box>
    </CommonModal>
  );
}
